import { authActions } from 'store/user'
import { actions as notyActions } from 'layouts/ErrorBox'
import { loadingActions } from 'store/loader'
import { sendingActions } from 'store/sender'
import { resolveControlApiUrl } from './controlApiUrl'
import { logSentryEvent } from './sentry'
import { AUTH_REQUIRED_MESSAGE, getInvalidSessionMessage } from './sessionUtils'

const _defaultMessages = {
  '500': 'The server is experiencing technical difficulties. Please try again later.',
  '401': AUTH_REQUIRED_MESSAGE,
  '404': 'Page or resource not found.',
}

export const generateNotyMessage = (message, success = true, icon) => {
  const defaultIcon = success ? 'far fa-check-circle' : 'far fa-times-circle'
  const notyIcon = icon || defaultIcon
  return `<div class='flexed centered-aligned' ><i class="first ${notyIcon}"></i><span class="message-text">${message}</span></div><span>Clear</span>`
}

// default fail behavior is to display a failmessage via noty if it exists
// otherwise shows the server message
// customFail will replace all default behavior

const _defaultFail = (response, resContent, customFail, failMessage, hasSender) => {
  return dispatch => {
    let content = {}
    // Error bodies are not guaranteed to be JSON (can be HTML on proxies/redirects),
    // so parsing must be defensive to avoid "Unexpected token '<'".
    try {
      if (typeof resContent === 'string') {
        content = resContent ? JSON.parse(resContent) : {}
      } else if (typeof resContent === 'object' && resContent !== null) {
        content = resContent
      }
    } catch (e) {
      content = { message: typeof resContent === 'string' ? resContent : _defaultMessages[response?.status] }
    }
    if (hasSender) {
      dispatch(sendingActions.resetSender())
    }
    if (customFail) {
      return customFail(response, content)
    }
    let errMessage = failMessage || _defaultMessages[response.status]
    errMessage = errMessage || content.message || `${response.status} Error: ${response.statusText}.`
    logSentryEvent({
      message: 'API request failed',
      level: 'error',
      tags: {
        'http.status': response?.status,
      },
      contexts: {
        http: {
          url: response?.url || '',
        },
      },
      breadcrumb: {
        category: 'http',
        message: 'API request failed',
        level: 'error',
      },
    })
    dispatch(notyActions.showError({ text: generateNotyMessage(errMessage, false) }))
  }
}

// default success behavior will trigger a noty message with successMessage if it is passed

const _getDefaultSuccess = successMessage => {
  return () => {
    return dispatch => {
      if (successMessage) dispatch(notyActions.showSuccess({ text: successMessage }))
    }
  }
}

const isFetchNetworkError = err => {
  if (!(err instanceof TypeError)) return false

  return /failed to fetch|networkerror|load failed/i.test(err.message || '')
}

// validate the session and returns a promise
export const validateSession = (dispatch, user, callback, getState) => {
  const { location } = getState()
  const isPasswordRecoveryFlow = ['/users/activate', '/users/reset'].includes(location?.pathname)

  const handleInvalidSession = err => {
    dispatch(authActions.logout(user))
    if (!isPasswordRecoveryFlow) {
      const message = err?.message || AUTH_REQUIRED_MESSAGE
      dispatch(notyActions.showError({ text: generateNotyMessage(message, false), killer: true }))
    }
  }

  return new Promise((resolve, reject) => {
    const errMessage = getInvalidSessionMessage(user)
    if (errMessage) throw new Error(errMessage)
    else resolve()
  })
    .then(callback)
    .catch(handleInvalidSession)
}

const parseOutFileName = contentDispositionString => {
  const fileName =
    contentDispositionString?.split('filename=')[1]?.split(';')[0] ||
    contentDispositionString?.split('filename = ')[1]?.split(';')[0]
  const resultFileName = fileName.replace(/"/g, '')
  return resultFileName
}

const { isDcfApiPath } = require('../../shared/dcfApiPaths')

/**
 * DCF V2 APIs go through API Gateway (Cognito authorizer) and expect
 * `Authorization: Bearer <token>`. Control-server APIs still expect the raw token.
 *
 * Path matching lives in shared/dcfApiPaths.js (same rules as the dev proxy).
 */
const formatAuthorizationHeader = (token, url) => {
  if (!token) return undefined
  if (!isDcfApiPath(url)) return token
  if (/^Bearer\s+/i.test(token)) return token
  return `Bearer ${token}`
}

const request = ({
  method = 'GET',
  url,
  body,
  headerOptions,
  contentType = 'application/json',
  credentials = true,
  success,
  fail,
  failMessage,
  successMessage,
  skipNotySuccess,
  catchMessage,
  catchFail,
  forceLoader = false,
  authRequired = true,
  resType = 'json', // currently supports only blob or json responses
  hasLoader = false,
  hasSender = false,
  loadingKey,
  id,
  userData,
  manualToken,
  successIcon,
  logoutOnUnauthorized,
}) => {
  return (dispatch, getState) => {
    if (hasLoader) {
      if (loadingKey) {
        dispatch(loadingActions.startLoader(true, loadingKey, id))
      } else {
        dispatch(loadingActions.startLoader(forceLoader))
      }
    }
    const resolveLoader = () => {
      if (hasLoader) {
        if (loadingKey) {
          dispatch(loadingActions.stopLoader(true, loadingKey, id))
        } else {
          dispatch(loadingActions.stopLoader(forceLoader))
        }
      }
    }

    const user = userData || getState().user
    const callback = () => {
      // track response
      let response

      // establish default success behavior (print success message if provided)
      success = success || _getDefaultSuccess(successMessage)

      // setup headers
      const headers = {}
      const token = manualToken || user.token || user.access_token
      if (token) {
        headers.Authorization = formatAuthorizationHeader(token, url)
      }
      if (contentType) headers['Content-Type'] = contentType
      // End-to-end trace id: UI → dcf-service → database-service
      const traceId =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `trace-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      headers['X-Trace-Id'] = traceId
      Object.assign(headers, headerOptions)

      if (contentType === 'multipart/form-data') {
        delete headers['Content-Type']
      }

      const options = { method, headers, body }
      // this makes cookies (access_token) get sent
      if (credentials) {
        options.credentials = 'include'
      }

      const resolvedUrl = resolveControlApiUrl(url)
      // make fetch request
      return fetch(resolvedUrl, options)
        .then(async res => {
          response = res
          const responseTraceId = res.headers.get('x-trace-id') || traceId
          if (typeof __DEV__ !== 'undefined' && __DEV__) {
            // eslint-disable-next-line no-console
            console.debug(`[trace] ${method} ${url} → ${res.status} trace_id=${responseTraceId}`)
          }
          if (res.ok) {
            if (resType === 'json') {
              const contentType = (res.headers.get('content-type') || '').toLowerCase()
              if (contentType.includes('text/html')) {
                const html = await res.text()
                return Promise.reject({
                  response: res,
                  error: JSON.stringify({
                    message:
                      'Expected JSON from API but received HTML. ' +
                      (isDcfApiPath(url)
                        ? 'DCF proxy may be down — restart adminpanel (npm start) so /dcf routes to dcf-service.'
                        : 'Control API proxy may be down — check HPI_CTRL_HOST and routing for /control/*.'),
                    code: 'api_returned_html',
                    preview: html.slice(0, 120),
                  }),
                  status: 502,
                  traceId: responseTraceId,
                })
              }
              return res.json()
            }
            return resType === 'text' ? res.text() : res.blob()
          }
          const errorText = await res.text()
          return Promise.reject({
            response: res,
            error: errorText,
            status: res.status,
            traceId: responseTraceId,
          })
        })
        .then(resContent => {
          // parses out a filename if there is one
          let fileName = ''
          const contentDisposition = response.headers.get('content-disposition')
          if (contentDisposition) fileName = parseOutFileName(contentDisposition)

          let nextStep
          if (response.ok) {
            if (!skipNotySuccess && successMessage)
              setTimeout(
                () => {
                  dispatch(
                    notyActions.showSuccess({
                      text: generateNotyMessage(successMessage, true, successIcon),
                    }),
                  )
                },
                hasSender ? 1000 : 0,
              )
            nextStep = success(resContent, fileName)
          } else {
            nextStep = dispatch(_defaultFail(response, resContent, fail, failMessage, hasSender))
          }
          resolveLoader()
          return nextStep || true // return default true to handle conditional promise chaining
        })
        .catch(err => {
          if (typeof err === 'object') {
            const { response, error, status } = err
            const statusStr = status && status.toString()

            if (statusStr === '401') {
              if (logoutOnUnauthorized === false) {
                resolveLoader()
                return dispatch(_defaultFail(response, error, fail, failMessage, hasSender))
              }
              dispatch(authActions.logout(user))
              resolveLoader()
              return true
            }

            // Handle all 4xx/5xx so validation errors (e.g. 422) are shown in UI
            if (status >= 400) {
              resolveLoader()
              return dispatch(_defaultFail(response, error, fail, failMessage, hasSender))
            }
          }
          if (catchMessage) dispatch(notyActions.showError({ text: generateNotyMessage(catchMessage, false) }))
          if (catchFail) dispatch(catchFail(err))
          const isNetworkError = isFetchNetworkError(err)
          const sentryEvent = {
            message: 'Network error',
            level: isNetworkError ? 'warning' : 'error',
            tags: {
              'error.kind': 'network',
            },
            contexts: {
              http: {
                url: resolvedUrl || '',
                method,
              },
            },
          }
          if (!isNetworkError) sentryEvent.error = err
          logSentryEvent(sentryEvent)
          resolveLoader()
          if (typeof __DEV__ !== 'undefined' && __DEV__ && !isNetworkError) console.error(err)
        })
    }
    if (!authRequired) return callback()
    return validateSession(dispatch, user, callback, getState)
  }
}

export default request
